import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { dashboardMetrics } from "@/lib/data";
import {
	Send,
	Eye,
	MessageSquare,
	AlertTriangle,
	TrendingUp,
	Mail,
	Linkedin,
	Smartphone,
} from "lucide-react";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	BarChart,
	Bar,
	Cell,
} from "recharts";

const m = dashboardMetrics;

const statCards = [
	{ label: "Total Sent", value: m.totalSent.toLocaleString(), icon: Send, color: "text-neon-cyan", bg: "bg-neon-cyan/10", glow: "" },
	{ label: "Opened", value: m.totalOpened.toLocaleString(), sub: `${m.openRate}%`, icon: Eye, color: "text-neon-purple", bg: "bg-neon-purple/10", glow: "" },
	{ label: "Replied", value: m.totalReplied.toLocaleString(), sub: `${m.replyRate}%`, icon: MessageSquare, color: "text-neon-green", bg: "bg-neon-green/10", glow: "" },
	{ label: "Bounced", value: m.totalBounced.toLocaleString(), sub: `${m.bounceRate}%`, icon: AlertTriangle, color: "text-neon-pink", bg: "bg-neon-pink/10", glow: "" },
];

const channelIcons: Record<string, React.ReactNode> = {
	email: <Mail className="size-4" />,
	linkedin: <Linkedin className="size-4" />,
	sms: <Smartphone className="size-4" />,
};

const channelColors = ["hsl(180,100%,50%)", "hsl(270,100%,65%)", "hsl(150,100%,50%)"];

function CustomTooltip({ active, payload, label }: any) {
	if (!active || !payload?.length) return null;
	return (
		<div className="bg-card border border-border rounded-lg p-3 shadow-xl">
			<p className="text-xs text-muted-foreground mb-1">{label}</p>
			{payload.map((p: any) => (
				<p key={p.name} className="text-sm" style={{ color: p.color }}>
					{p.name}: <span className="font-semibold">{p.value}</span>
				</p>
			))}
		</div>
	);
}

export function Dashboard() {
	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold">Performance Insights</h1>
				<p className="text-muted-foreground text-sm mt-1">
					Real-time analytics across all your sequences
				</p>
			</div>

			{/* Stat Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
				{statCards.map((card) => (
					<Card key={card.label} className={`${card.glow} border-border/50`}>
						<CardContent className="pt-5 pb-4 px-5">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-xs text-muted-foreground uppercase tracking-wide">
										{card.label}
									</p>
									<p className="text-2xl font-bold mt-1">{card.value}</p>
									{card.sub && (
										<Badge variant="secondary" className="mt-1 text-xs">
											{card.sub} rate
										</Badge>
									)}
								</div>
								<div className={`${card.bg} rounded-xl p-3`}>
									<card.icon className={`size-5 ${card.color}`} />
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Charts Row */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
				{/* Activity Chart */}
				<Card className="lg:col-span-2 border-border/50">
					<CardContent className="pt-5">
						<div className="flex items-center justify-between mb-4">
							<div>
								<h3 className="font-semibold">Daily Activity</h3>
								<p className="text-xs text-muted-foreground">Last 10 days</p>
							</div>
							<TrendingUp className="size-4 text-neon-green" />
						</div>
						<div className="h-64">
							<ResponsiveContainer width="100%" height="100%">
								<AreaChart data={m.dailyActivity}>
									<defs>
										<linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="hsl(180,100%,50%)" stopOpacity={0.3} />
											<stop offset="95%" stopColor="hsl(180,100%,50%)" stopOpacity={0} />
										</linearGradient>
										<linearGradient id="gradOpened" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="hsl(270,100%,65%)" stopOpacity={0.3} />
											<stop offset="95%" stopColor="hsl(270,100%,65%)" stopOpacity={0} />
										</linearGradient>
										<linearGradient id="gradReplied" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="hsl(150,100%,50%)" stopOpacity={0.3} />
											<stop offset="95%" stopColor="hsl(150,100%,50%)" stopOpacity={0} />
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray="3 3" stroke="hsl(230,15%,16%)" />
									<XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(230,10%,50%)" }} axisLine={false} tickLine={false} />
									<YAxis tick={{ fontSize: 11, fill: "hsl(230,10%,50%)" }} axisLine={false} tickLine={false} />
									<Tooltip content={<CustomTooltip />} />
									<Area type="monotone" dataKey="sent" stroke="hsl(180,100%,50%)" fill="url(#gradSent)" strokeWidth={2} name="Sent" />
									<Area type="monotone" dataKey="opened" stroke="hsl(270,100%,65%)" fill="url(#gradOpened)" strokeWidth={2} name="Opened" />
									<Area type="monotone" dataKey="replied" stroke="hsl(150,100%,50%)" fill="url(#gradReplied)" strokeWidth={2} name="Replied" />
								</AreaChart>
							</ResponsiveContainer>
						</div>
					</CardContent>
				</Card>

				{/* Channel Breakdown */}
				<Card className="border-border/50">
					<CardContent className="pt-5">
						<h3 className="font-semibold mb-4">Channel Breakdown</h3>
						<div className="h-48">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={m.channelBreakdown} layout="vertical" barGap={4}>
									<CartesianGrid strokeDasharray="3 3" stroke="hsl(230,15%,16%)" horizontal={false} />
									<XAxis type="number" tick={{ fontSize: 11, fill: "hsl(230,10%,50%)" }} axisLine={false} tickLine={false} />
									<YAxis dataKey="channel" type="category" tick={{ fontSize: 11, fill: "hsl(230,10%,50%)" }} axisLine={false} tickLine={false} width={60} />
									<Tooltip content={<CustomTooltip />} />
									<Bar dataKey="sent" name="Sent" radius={[0, 4, 4, 0]}>
										{m.channelBreakdown.map((_, i) => (
											<Cell key={i} fill={channelColors[i]} fillOpacity={0.7} />
										))}
									</Bar>
								</BarChart>
							</ResponsiveContainer>
						</div>
						<div className="mt-4 space-y-3">
							{m.channelBreakdown.map((ch, i) => (
								<div key={ch.channel} className="flex items-center justify-between text-sm">
									<div className="flex items-center gap-2">
										<div className="size-2 rounded-full" style={{ backgroundColor: channelColors[i] }} />
										{channelIcons[ch.channel]}
										<span className="capitalize">{ch.channel}</span>
									</div>
									<span className="text-muted-foreground">
										{ch.replied} replies / {ch.sent} sent
									</span>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Top Sequences */}
			<Card className="border-border/50">
				<CardContent className="pt-5">
					<h3 className="font-semibold mb-4">Top Performing Sequences</h3>
					<div className="space-y-3">
						{m.topSequences.map((seq, i) => (
							<div key={seq.name} className="flex items-center gap-4">
								<span className="text-xs text-muted-foreground w-4 shrink-0">#{i + 1}</span>
								<div className="flex-1">
									<div className="flex items-center justify-between mb-1">
										<span className="text-sm font-medium">{seq.name}</span>
										<span className="text-sm text-neon-green font-semibold">{seq.replyRate}%</span>
									</div>
									<div className="h-1.5 bg-secondary rounded-full overflow-hidden">
										<div
											className="h-full rounded-full bg-gradient-to-r from-neon-cyan to-neon-green"
											style={{ width: `${seq.replyRate * 5}%` }}
										/>
									</div>
								</div>
								<span className="text-xs text-muted-foreground w-20 text-right">{seq.sent} sent</span>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
